import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiMail } from 'react-icons/fi';

import AuthLayout from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import { requestPasswordReset } from '../api/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      // The server answers identically whether or not the address exists, and
      // so does this screen — otherwise the UI would leak what the API won't.
      setSent(true);
    } catch (err) {
      setFormError(
        err.status
          ? err.message
          : 'Couldn’t reach the server. Check your connection and try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      aside={
        <>
          <h2 className="mm-auth__aside-title">Reset your password</h2>
          <p className="mm-auth__aside-text">
            We&rsquo;ll email you a link that&rsquo;s good for 30 minutes. Using
            it signs out any other reset links you&rsquo;ve requested.
          </p>
          <p className="mm-auth__aside-note">
            Didn&rsquo;t request this? You can ignore the email &mdash; nothing
            changes until the link is used.
          </p>
        </>
      }
    >
      {sent ? (
        <>
          <span className="mm-apply__done-icon d-inline-grid mb-3" aria-hidden="true">
            <FiCheckCircle size={26} />
          </span>
          <h1 className="mm-auth__title">Check your email</h1>
          <p className="mm-auth__subtitle">
            If there&rsquo;s an account for <strong>{email}</strong>, a reset
            link is on its way. It expires in 30 minutes.
          </p>
          <div className="mm-auth__footer">
            <Link to="/login">Back to log in</Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="mm-auth__title">Forgot your password?</h1>
          <p className="mm-auth__subtitle">
            Enter your email and we&rsquo;ll send you a link to set a new one.
          </p>

          <form className="mm-auth__form" onSubmit={handleSubmit} noValidate>
            {formError && (
              <div className="mm-form-alert mm-form-alert--error" role="alert">
                <FiAlertCircle size={16} aria-hidden="true" />
                <span>{formError}</span>
              </div>
            )}

            <TextField
              label="Email address"
              name="email"
              type="email"
              icon={FiMail}
              placeholder="you@example.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              error={error}
            />

            <Button type="submit" className="mm-auth__submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                'Send reset link'
              )}
            </Button>
          </form>

          <div className="mm-auth__footer">
            Remembered it? <Link to="/login">Back to log in</Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
